import { MyReport } from './MyReportsTable.types';
import { ReportAccessData, SavedReportsList } from '../../../@types';
import { SavedReportTypeList } from '../../../configs/enums';

export type SortDirection = 'asc' | 'desc';

export interface MyReportsSortModel {
  field: string;
  direction: SortDirection;
}

/** Which group of reports the list is showing. */
export type MyReportsScope = 'all' | 'mine' | 'shared' | 'other';

export const ALL_PAGES = 'all';

export interface MyReportsFilterState {
  name: string;
  /** A single report-type key, or ALL_PAGES. Driven by the page toggles. */
  pageName: string;
  ownerIds: string[];
  scope: MyReportsScope;
}

export const EMPTY_FILTERS: MyReportsFilterState = {
  name: '',
  pageName: ALL_PAGES,
  ownerIds: [],
  scope: 'all',
};

/** The report-type key a row is filed under, used by both the toggles and the filter. */
export const getPageKey = (row: SavedReportsList): string =>
  row.savedReportType?.name ?? row.pageName ?? '';

// Human-readable label for each report-type enum value. Anything not listed
// falls back to the raw enum string so the UI never shows nothing.
const PAGE_NAME_LABELS: Record<string, string> = {
  [SavedReportTypeList.ServiceList]: 'Jobs',
  [SavedReportTypeList.StructureList]: 'Structures',
  [SavedReportTypeList.PartList]: 'Parts',
  [SavedReportTypeList.UserList]: 'Users',
  [SavedReportTypeList.ClientContractList]: 'Clients & Contracts',
  [SavedReportTypeList.ServiceTypeList]: 'Service Types',
  [SavedReportTypeList.PartTypeList]: 'Part Types',
  [SavedReportTypeList.RoleList]: 'Roles',
  [SavedReportTypeList.AttendanceReportList]: 'Attendance',
  [SavedReportTypeList.PartMovementList]: 'Part Movements',
  [SavedReportTypeList.ReplenishmentReportList]: 'Replenishment',
};

export const getPageNameLabel = (pageName: string | undefined): string =>
  (pageName && PAGE_NAME_LABELS[pageName]) || pageName || '';

// The backend returns scheduled state as a free-text string ("Active: true, ...").
// Centralising the parse keeps the format assumption in one place so a format
// change only needs a single update.
export const isScheduleActive = (scheduledDetails: string | undefined): boolean =>
  !!scheduledDetails?.includes('Active: true');

export const isOwnedByMe = (row: SavedReportsList, mySiblingId: number | undefined): boolean =>
  mySiblingId != null && row.reportOwner?.id === mySiblingId;

export const isSharedWithMe = (row: SavedReportsList, mySiblingId: number | undefined): boolean =>
  mySiblingId != null &&
  !!row.savedReportAccess?.some(
    (access: ReportAccessData) => access.entityId === mySiblingId && access.isActive !== false
  );

/** Which of the three list groups a row belongs to. */
export const groupOfRow = (
  row: SavedReportsList,
  mySiblingId: number | undefined
): Exclude<MyReportsScope, 'all'> => {
  if (isOwnedByMe(row, mySiblingId)) return 'mine';
  if (isSharedWithMe(row, mySiblingId)) return 'shared';
  return 'other';
};

/**
 * Whether this user is allowed to see the row at all.
 *
 * Client/customer users get a curated view: reports shared elsewhere in their
 * hierarchy (the "Other" group) are withheld. Deciding that once, here, is what
 * lets the grouping, the totals and the filter dropdown options all agree —
 * previously the dropdowns were built from the raw row set, so a client saw
 * owner and page names belonging to reports their table never showed.
 */
export const isVisibleToUser = (
  row: SavedReportsList,
  mySiblingId: number | undefined,
  isClient: boolean
): boolean => {
  if (!isClient) return true;
  return isOwnedByMe(row, mySiblingId) || isSharedWithMe(row, mySiblingId);
};

/**
 * The fields the free-text box searches. Users type what they can see, so the
 * page match is against the human label ("Jobs"), not the enum ("ServiceList").
 *
 * `sharedWith` and `scheduledDetails` are internal-only — their columns are
 * hidden from clients, so searching them for a client would turn the box into an
 * oracle that confirms recipient names the view deliberately withholds.
 */
const matchesQuickFind = (
  row: MyReport,
  needle: string,
  includeInternalFields: boolean
): boolean => {
  const haystack = [
    row.name,
    // The report ID exists to be quoted between users, so pasting one back in
    // has to find its report.
    row.code,
    getPageNameLabel(row.savedReportType?.name ?? row.pageName ?? ''),
    row.reportOwner?.name,
    ...(includeInternalFields ? [row.sharedWith, row.scheduledDetails] : []),
  ];
  return haystack.some((value) => !!value && value.toLowerCase().includes(needle));
};

/** Which criteria to apply. Omitting one is how a toggle group counts its own options. */
export interface FilterCriteria {
  name?: boolean;
  page?: boolean;
  owner?: boolean;
  scope?: boolean;
}

const ALL_CRITERIA: Required<FilterCriteria> = { name: true, page: true, owner: true, scope: true };

export interface ApplyFiltersOptions {
  includeInternalFields?: boolean;
  mySiblingId?: number | undefined;
  /** Defaults to every criterion. */
  only?: FilterCriteria;
}

export const applyFilters = (
  rows: MyReport[],
  filters: MyReportsFilterState,
  { includeInternalFields = false, mySiblingId, only }: ApplyFiltersOptions = {}
): MyReport[] => {
  const use = only ? { ...ALL_CRITERIA, ...only } : ALL_CRITERIA;
  const nameNeedle = filters.name.trim().toLowerCase();
  const hasName = use.name && nameNeedle.length > 0;
  const hasPage = use.page && filters.pageName !== ALL_PAGES;
  const hasOwner = use.owner && filters.ownerIds.length > 0;
  const hasScope = use.scope && filters.scope !== 'all';

  if (!hasName && !hasPage && !hasOwner && !hasScope) return rows;

  return rows.filter((row) => {
    if (hasName && !matchesQuickFind(row, nameNeedle, includeInternalFields)) return false;
    if (hasPage && getPageKey(row) !== filters.pageName) return false;
    if (hasOwner && !filters.ownerIds.includes(String(row.reportOwner?.id ?? ''))) return false;
    if (hasScope && groupOfRow(row, mySiblingId) !== filters.scope) return false;
    return true;
  });
};

export const hasAnyActiveFilter = (filters: MyReportsFilterState): boolean =>
  filters.name.trim().length > 0 ||
  filters.pageName !== ALL_PAGES ||
  filters.ownerIds.length > 0 ||
  filters.scope !== 'all';

/**
 * Counts for one toggle group, computed over the rows that pass *every other*
 * filter. That is what makes a count a promise: it says how many rows you get if
 * you click this option, given everything else you have already chosen.
 */
export const countBy = <T extends string>(rows: MyReport[], key: (row: MyReport) => T) => {
  const counts = new Map<T, number>();
  for (const row of rows) {
    const k = key(row);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
};

export const compareRows = (
  a: SavedReportsList,
  b: SavedReportsList,
  { field, direction }: MyReportsSortModel
): number => {
  const dir = direction === 'asc' ? 1 : -1;
  const va = getSortValue(a, field);
  const vb = getSortValue(b, field);

  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (va < vb) return -1 * dir;
  if (va > vb) return 1 * dir;
  return 0;
};

const getSortValue = (row: SavedReportsList, field: string): string | number | null => {
  switch (field) {
    case 'name':
      return (row.name ?? '').toLowerCase();
    case 'pageName':
      return getPageNameLabel(row.savedReportType?.name ?? row.pageName ?? '').toLowerCase();
    case 'reportOwner':
      return (row.reportOwner?.name ?? '').toLowerCase();
    case 'createdAt': {
      if (!row.createdAt) return null;
      const ts = new Date(row.createdAt).getTime();
      return Number.isFinite(ts) ? ts : null;
    }
    default:
      return null;
  }
};
